import React, { Fragment } from 'react';
import type { StyledProps } from '@compiled/react';
import { css, cssMap, styled } from '@compiled/react';
import uniqBy from 'lodash/uniqBy';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { HOME_OVERVIEW_RECENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { ExperienceStop } from '@confluence/experience-tracker/entry-points/component.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { RECENT_WORK_ITEM_ON_HOME_PAGE_CLICK } from '@confluence/browser-metrics/entry-points/viewPageTransitionSourceConstants.ts';
import { setViewTransitionSource } from '@confluence/browser-metrics/entry-points/viewTransitionSource.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils/entry-points/useIsWhiteboardFeatureEnabled.ts';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv.ts';

import { useRightPanelVisible } from '../../RightPanel/useRightPanelVisible.ts';
import { HOME_OVERVIEW_RECENT_PAGE_SEGMENT } from '../../../perf.config.ts';
import {
	transformActivityData,
	notEmpty,
	notArchived,
	notDeleted,
} from '../homeViewsQueryHelpers.ts';

import type { RecentListHomeRecentWorkV3Query as RecentListHomeRecentWorkV3Query$data } from './__types__/RecentListHomeRecentWorkV3Query.ts';
import { RecentListItem } from './RecentListItem.tsx';
import { RecentWorkEmptyState } from './RecentWorkEmptyState.tsx';
import { ALL_RECENT_ITEMS_COUNT_HOME_REFRESH } from './recentListConstants.ts';
import { useHomeRecentWorkData } from './useHomeRecentWorkData.ts';

const gridContainerStyles = cssMap({
	default: {
		display: 'grid',
		gridTemplateRows: 'repeat(calc(2 + var(--homeIsSingleColumn), auto))',
		gap: token('space.200'),
		gridTemplateColumns: 'repeat(calc(3 - var(--homeIsSingleColumn)), minmax(0, 1fr))',
	},
	shouldShowRightPanel: {
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
	},
});

const agenticSessionsStyles = cssMap({
	list: {
		display: 'flex',
		flexDirection: 'column',
		rowGap: token('space.050'),
	},
	itemPlaceholder: {
		display: 'flex',
		alignItems: 'center',
		minHeight: '56px',
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
	},
	iconPlaceholder: {
		flexShrink: 0,
		width: '32px',
		height: '32px',
		marginInlineEnd: token('space.150'),
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.small'),
	},
	titleAndMetadataPlaceholder: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minWidth: 0,
		rowGap: token('space.050'),
	},
	titlePlaceholder: {
		width: '45%',
		height: '16px',
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.small'),
	},
	metadataPlaceholder: {
		width: '30%',
		height: '12px',
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.small'),
	},
	lastActionPlaceholder: {
		flexShrink: 0,
		width: '88px',
		height: '12px',
		marginInlineStart: token('space.200'),
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.small'),
	},
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TitleAndCreatorAndSpace: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.div({
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	flexShrink: 1,
	justifyContent: 'space-between',
	minWidth: '200px',
	marginRight: token('space.150'),
});
const titleAndCreatorAndSpaceStyles = css({
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	flexShrink: 1,
	justifyContent: 'space-between',
	minWidth: '200px',
	marginRight: token('space.150'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ItemPlaceholder: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.div({
	display: 'flex',
	height: '44px',
	padding: `10px ${token('space.250')}`,
});
const itemPlaceholderStyles = css({
	display: 'flex',
	height: '44px',
	padding: `10px ${token('space.250')}`,
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const IconPlaceholder: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.div({
	width: '24px',
	height: '24px',
	marginTop: token('space.025'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginRight: '14px',
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
});
const iconPlaceholderStyles = css({
	width: '24px',
	height: '24px',
	marginTop: token('space.025'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginRight: '14px',
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TitlePlaceholder: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.div({
	height: '18px',
	width: '60%',
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
});
const titlePlaceholderStyles = css({
	height: '18px',
	width: '60%',
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CreatorAndSpacePlaceholder: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.div({
	height: '16px',
	width: '35%',
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
});
const creatorAndSpacePlaceholderStyles = css({
	height: '16px',
	width: '35%',
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const LastActionPlaceholder: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.div({
	height: '12px',
	width: '25%',
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	marginTop: token('space.025'),
});
const lastActionPlaceholderStyles = css({
	height: '12px',
	width: '25%',
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	marginTop: token('space.025'),
});

export const filteredRecents = (
	data: RecentListHomeRecentWorkV3Query$data | undefined,
	isShowingMore: boolean,
	firstPageSize: number = ALL_RECENT_ITEMS_COUNT_HOME_REFRESH,
): any[] => {
	const recents = uniqBy(
		(data?.activity?.myActivity?.all?.edges || [])
			.map((edge) => edge?.node)
			.map((node) => transformActivityData(node, true))
			.filter(notEmpty)
			.filter((node) => notEmpty(node.object?.data))
			.filter((node) => notArchived(node.object?.data))
			.filter((node) => notDeleted(node.object?.data)),
		(node) => node.object?.data?.id,
	);
	if (isShowingMore) {
		return recents;
	}
	return recents.slice(0, firstPageSize);
};

export const RecentList = (): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { shouldShowRightPanel } = useRightPanelVisible();
	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const isWhiteboardViewedEnabled = isWhiteboardFeatureEnabled('whiteboardsEnabled');
	const isDatabaseEnabled = isContentTypeEnabledInCurrentEnv('database');
	const isSlidesEnabled = isContentTypeEnabledInCurrentEnv('slide');

	const { data, loading, error } = useHomeRecentWorkData({
		isWhiteboardViewedEnabled,
		isDatabaseEnabled,
		isSlidesEnabled,
		first: ALL_RECENT_ITEMS_COUNT_HOME_REFRESH,
	});

	const recents = filteredRecents(data, false, ALL_RECENT_ITEMS_COUNT_HOME_REFRESH);
	const onItemClick = (
		index: number,
		action: string,
		status: string | undefined,
		contentId: string,
		spaceKey: string,
		tenantId: string,
		objectType: string,
	) => {
		if (objectType === 'page') {
			setViewTransitionSource(RECENT_WORK_ITEM_ON_HOME_PAGE_CLICK);
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'link',
				actionSubjectId: 'recentWorkItem',
				action: 'clicked',
				source: 'overviewScreen',
				attributes: {
					platformCtx: 'recentWork',
					destinationProduct: 'confluence',
					destinationTenantId: tenantId,
					destinationContainerType: 'space',
					destinationContainerId: spaceKey,
					destinationObjectType: objectType,
					destinationObjectId: contentId,
					pageLocation: 'home',
					ranking: index,
					navdexPointType: START_TOUCH,
					lastAction: action,
					contentStatus: status,
				},
			},
		}).fire();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'homeRecentWorkItem',
				action: 'clicked',
				source: 'overviewScreen',
				containerType: 'spaceKey',
				containerId: spaceKey,
				objectType,
				objectId: contentId,
				attributes: {
					ranking: index,
					navdexPointType: START_TOUCH,
					lastAction: action,
					contentStatus: status,
				},
			},
		}).fire();
	};
	const onItemSpaceClick = (index: number) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'homeRecentWorkItemSpace',
				action: 'clicked',
				source: 'overviewScreen',
				attributes: {
					ranking: index,
					navdexPointType: START_TOUCH,
				},
			},
		}).fire();
	};

	if (!loading && !recents.length) {
		return (
			<>
				<RecentWorkEmptyState />
				{error && <ErrorDisplay error={error} />}
				<ExperienceStop name={HOME_OVERVIEW_RECENT_EXPERIENCE} error={error} />
				<PageSegmentLoadEnd metric={HOME_OVERVIEW_RECENT_PAGE_SEGMENT} />
			</>
		);
	}
	if (expValEquals('cc_ai_agentic_sessions', 'isEnabled', true)) {
		return (
			<div
				css={agenticSessionsStyles.list}
				{...(!loading && recents.length > 0 && { role: 'list' })}
			>
				{loading && !recents.length
					? new Array(ALL_RECENT_ITEMS_COUNT_HOME_REFRESH).fill(0).map((_, i) => (
							<div
								css={agenticSessionsStyles.itemPlaceholder}
								key={i}
								data-testid="item-placeholder"
							>
								<div css={agenticSessionsStyles.iconPlaceholder} />
								<div css={agenticSessionsStyles.titleAndMetadataPlaceholder}>
									<div css={agenticSessionsStyles.titlePlaceholder} />
									<div css={agenticSessionsStyles.metadataPlaceholder} />
								</div>
								<div css={agenticSessionsStyles.lastActionPlaceholder} />
							</div>
						))
					: recents.map((recent, index) => (
							<RecentListItem
								key={recent.id}
								recent={recent}
								index={index}
								onItemClick={onItemClick}
								onItemSpaceClick={onItemSpaceClick}
								useAgenticSessionsAppearance
							/>
						))}
				{!loading && (
					<Fragment>
						{error && <ErrorDisplay error={error} />}
						<ExperienceStop name={HOME_OVERVIEW_RECENT_EXPERIENCE} error={error} />
						<PageSegmentLoadEnd metric={HOME_OVERVIEW_RECENT_PAGE_SEGMENT} />
					</Fragment>
				)}
			</div>
		);
	}

	return (
		<div
			css={[
				gridContainerStyles['default'],
				shouldShowRightPanel && gridContainerStyles['shouldShowRightPanel'],
			]}
			{...(!loading && recents.length > 0 && { role: 'list' })}
		>
			{loading && !recents.length
				? new Array(ALL_RECENT_ITEMS_COUNT_HOME_REFRESH).fill(0).map((_, i) => (
						<div css={itemPlaceholderStyles} key={i} data-testid="item-placeholder">
							<div css={iconPlaceholderStyles} />
							<div css={titleAndCreatorAndSpaceStyles}>
								<div css={titlePlaceholderStyles} />
								<div css={creatorAndSpacePlaceholderStyles} />
							</div>
							<div css={lastActionPlaceholderStyles} />
						</div>
					))
				: recents.map((recent, index) => (
						<RecentListItem
							key={recent.id}
							recent={recent}
							index={index}
							onItemClick={onItemClick}
							onItemSpaceClick={onItemSpaceClick}
							shouldAutofocus={index === ALL_RECENT_ITEMS_COUNT_HOME_REFRESH}
						/>
					))}
			{!loading && (
				<Fragment>
					{error && <ErrorDisplay error={error} />}
					<ExperienceStop name={HOME_OVERVIEW_RECENT_EXPERIENCE} error={error} />
					<PageSegmentLoadEnd metric={HOME_OVERVIEW_RECENT_PAGE_SEGMENT} />
				</Fragment>
			)}
		</div>
	);
};
